import SEO from "@/components/SEO";

const LAST_UPDATED = "September 8, 2026";

export default function Privacy() {
  return (
    <>
      <SEO
        pageKey="privacy"
        title="Privacy Policy — SerpNex"
        description="How SerpNex collects, uses, stores, and protects your personal information — plain English, transparent."
        canonical="/privacy"
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24">
        <div className="eyebrow">Legal</div>
        <h1 className="mt-3 h-display text-5xl sm:text-6xl font-bold text-[#0A0D14]">Privacy Policy</h1>
        <div className="mt-3 text-sm text-[#8A94A6]">Last updated: {LAST_UPDATED}</div>

        <div className="mt-10 space-y-8 text-[16px] leading-relaxed text-[#525B6A]">
          <section>
            <p>
              This Privacy Policy explains how SerpNex (&ldquo;<strong>SerpNex</strong>,&rdquo;
              &ldquo;<strong>we</strong>,&rdquo; &ldquo;<strong>us</strong>&rdquo;) collects, uses, discloses, and safeguards
              information when you visit <a href="https://serpnex.com" className="text-[#0047FF] hover:underline">serpnex.com</a>{" "}
              or use our keyword rank tracking service (the &ldquo;<strong>Service</strong>&rdquo;). By using the Service you
              agree to the practices described here.
            </p>
          </section>

          <section>
            <h2 className="h-display text-2xl font-semibold text-[#0A0D14] mb-3">1. Information we collect</h2>
            <p><strong>Account data.</strong> When you sign up we collect your name, company (optional), email address, and a hashed password. We never store your raw password.</p>
            <p className="mt-3"><strong>Billing data.</strong> Subscription payments are processed by PayPal. We receive a subscription identifier, plan, and status from PayPal — we do not see or store your card number, PayPal password, or bank details.</p>
            <p className="mt-3"><strong>Project data.</strong> The keywords, projects, target URLs, tags, notes, competitor domains, uploads, and reports you create in the Service.</p>
            <p className="mt-3"><strong>Usage data.</strong> Basic logs (IP address, browser type, timestamps, requested endpoints, error events) used to secure the Service, debug issues, and analyze aggregate usage.</p>
            <p className="mt-3"><strong>Communications.</strong> When you contact us, we retain the email address and content of the message so we can respond and improve support.</p>
          </section>

          <section>
            <h2 className="h-display text-2xl font-semibold text-[#0A0D14] mb-3">2. How we use your information</h2>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Provide, operate, and secure the Service (authentication, rank checks, PDF exports, alerts).</li>
              <li>Process payments and manage your subscription with PayPal.</li>
              <li>Send transactional email (login, password reset, scheduled reports, alerts) via Resend.</li>
              <li>Diagnose crashes, prevent abuse, and improve reliability.</li>
              <li>Comply with legal obligations and enforce our Terms.</li>
            </ul>
            <p className="mt-3">We do <strong>not</strong> sell your personal information, and we do not use your project data to train AI models.</p>
          </section>

          <section>
            <h2 className="h-display text-2xl font-semibold text-[#0A0D14] mb-3">3. Third-party sub-processors</h2>
            <p>We share the minimum information needed with these providers under written data-protection commitments:</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-3">
              <li><strong>PayPal</strong> — subscription billing and webhook processing.</li>
              <li><strong>DataForSEO</strong> — retrieves search-engine ranking data for the keywords you track. Only keyword text, country, language, device, and engine are sent — never your account credentials.</li>
              <li><strong>Resend</strong> — transactional email delivery.</li>
              <li><strong>Emergent Cloud</strong> — hosting infrastructure (Kubernetes) and managed MongoDB.</li>
            </ul>
          </section>

          <section>
            <h2 className="h-display text-2xl font-semibold text-[#0A0D14] mb-3">4. Cookies &amp; local storage</h2>
            <p>We use cookies and browser local storage to keep you signed in, remember your dashboard preferences (e.g., column visibility, focus filters), and to run basic analytics. Local storage stores your login token (JWT) so you don&apos;t have to log in on every visit. You can clear cookies and local storage at any time via your browser settings — doing so will log you out.</p>
          </section>

          <section>
            <h2 className="h-display text-2xl font-semibold text-[#0A0D14] mb-3">5. Data retention</h2>
            <p>We retain account and project data for as long as your account is active. If you cancel, we retain the data for up to 90 days to allow for reactivation and legal recordkeeping. You may request earlier deletion by emailing <a href="mailto:contact@serpnex.com" className="text-[#0047FF] hover:underline">contact@serpnex.com</a>. Billing records may be retained longer as required by tax and financial regulations.</p>
          </section>

          <section>
            <h2 className="h-display text-2xl font-semibold text-[#0A0D14] mb-3">6. Security</h2>
            <p>We use HTTPS end-to-end, bcrypt-hashed passwords, JWT access tokens, and role-based authorization. Infrastructure secrets live in environment variables, not source code. No system is 100% secure — if you believe your account has been compromised, email <a href="mailto:contact@serpnex.com" className="text-[#0047FF] hover:underline">contact@serpnex.com</a> immediately.</p>
          </section>

          <section>
            <h2 className="h-display text-2xl font-semibold text-[#0A0D14] mb-3">7. Your rights</h2>
            <p>Depending on your jurisdiction (e.g., GDPR in the EU/UK, CCPA in California), you may have the right to access, correct, export, or delete personal information we hold about you, and to object to or restrict certain processing. Email <a href="mailto:contact@serpnex.com" className="text-[#0047FF] hover:underline">contact@serpnex.com</a> to exercise these rights. We will respond within 30 days.</p>
          </section>

          <section>
            <h2 className="h-display text-2xl font-semibold text-[#0A0D14] mb-3">8. International transfers</h2>
            <p>Our servers and sub-processors may be located outside your country of residence. Where required, we rely on Standard Contractual Clauses or equivalent safeguards for cross-border data transfers.</p>
          </section>

          <section>
            <h2 className="h-display text-2xl font-semibold text-[#0A0D14] mb-3">9. Children</h2>
            <p>The Service is not directed at children under 18 and we do not knowingly collect personal information from anyone under 18. If you believe a child has provided us information, please contact us and we will delete it.</p>
          </section>

          <section>
            <h2 className="h-display text-2xl font-semibold text-[#0A0D14] mb-3">10. Changes to this Policy</h2>
            <p>We may update this Policy from time to time. Material changes will be signaled by updating the &ldquo;Last updated&rdquo; date and, where appropriate, by email or in-app notice. Please review this page periodically.</p>
          </section>

          <section>
            <h2 className="h-display text-2xl font-semibold text-[#0A0D14] mb-3">11. Contact</h2>
            <p>Questions or requests about privacy? Email <a href="mailto:contact@serpnex.com" className="text-[#0047FF] hover:underline">contact@serpnex.com</a>.</p>
          </section>
        </div>
      </div>
    </>
  );
}
